import React, { useEffect, useCallback, useState, Fragment, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { TopNav } from 'shared/components/TopNav/index.js';
import { getUrlToSignupPage, getUrlToLandingPage, getUrlToLoginCallbackPage, getUrlToPhoneLoginPage, getUrlToGoogleLoginPage } from "../urls";
import { SpinnerFullPage } from 'shared/components/SpinnerFullPage/index.js';
import googleIconSrc from "../../images/icons/google.svg";
import smsIconSrc from "images/icons/sms.svg";
import antidoteLogoSrc from "../../images/antidote-logo.png";
import { LoginForm } from "./containers/LoginForm";
import { Divider } from "components/Divider";
import { OptionButton } from "components/OptionButton";
import { SignupLoginAgreement } from "containers/SignupLoginAgreement";
import { clearUserToken, isLoggedIn } from "api/auth-storage";

const SIGNUP_PAGE_URL = getUrlToSignupPage();
const LOGIN_CALLBACK_PAGE_URL = getUrlToLoginCallbackPage();
const LOGIN_WITH_PHONE_PAGE_URL = getUrlToPhoneLoginPage();
const LOGIN_WITH_GOOGLE_PAGE_URL = getUrlToGoogleLoginPage();

const showPhoneLogin = false;

export function LoginPage () {
  const emailInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const isAuthenticated = isLoggedIn();
  const [urlSearchParams] = useSearchParams();
  const [displayInsecureBrowserMessage, setDisplayInsecureBrowserMessage] = useState(false);

  const loginCallbackPageUrl = LOGIN_CALLBACK_PAGE_URL + '?' + urlSearchParams.toString();
  const signupPageUrl = SIGNUP_PAGE_URL + '?' + urlSearchParams.toString();
  const loginWithPhonePageUrl = LOGIN_WITH_PHONE_PAGE_URL + '?' + urlSearchParams.toString();
  const loginWithGooglePageUrl = LOGIN_WITH_GOOGLE_PAGE_URL + '?' + urlSearchParams.toString();

  const force = !!urlSearchParams.get('force');

  const redirectAfterLogin = useCallback(async () => {
    navigate(loginCallbackPageUrl)
  }, [loginCallbackPageUrl]);

  const handleGoogleSignIn = async () => {
    const isInsecureBrowser = checkInsecureBrowser();
    if (isInsecureBrowser) {
      setDisplayInsecureBrowserMessage(true);
      return;
    }
    navigate(loginWithGooglePageUrl);
  };

  const gotoLandingPage = () => {
    window.location.href = getUrlToLandingPage();
  };

  useEffect(() => {
    if (force) clearUserToken();
  }, [force]);

  useEffect(() => {
    if (!isAuthenticated) return;
    redirectAfterLogin();
  }, [isAuthenticated, force]);

  if (isAuthenticated) {
    return <SpinnerFullPage />;
  }

  return (
    <div className="px-6 py-4">
      <TopNav onBackClick={gotoLandingPage} />
      <div className="max-w-[350] mx-auto flex flex-col justify-center mt-12 gap-2">
        <img src={antidoteLogoSrc} alt='Antidote Logo' className="w-full mb-4" />
        {displayInsecureBrowserMessage ? (
          <Fragment>
            <h1>Hey there!</h1>
            <p>
              It looks like you are using a browser inside of an app like Instagram or Facebook.
            </p>
            <p>
              It is not possible to use Google login with this browser because Google considers it insecure.
            </p>
            <p>
              Please navigate to <a className="text-violet-600" target="_blank" href="https://antidotenepal.com">https://antidotenepal.com</a> from your system's browser (Chrome, Firefox, Opera, Safari, Edge, etc.) to use google login.
            </p>
            <p>
              You can also{' '}
              <span
                className="text-violet-600 cursor-pointer"
                onClick={() => {
                  setDisplayInsecureBrowserMessage(false);
                  setTimeout(() => emailInputRef.current?.focus(), 300);
                }}
              >try other login methods</span>.
            </p>
          </Fragment>
        ) : (
          <Fragment>
            <div>
              <OptionButton label="Continue with Google" iconImgSrc={googleIconSrc} onClick={handleGoogleSignIn} />
              {showPhoneLogin ? (
                <div className='mt-2'>
                  <Link to={loginWithPhonePageUrl}>
                    <OptionButton label="Continue with SMS OTP" iconImgSrc={smsIconSrc} />
                  </Link>
                </div>
              ) : null}
            </div>
            <Divider label="or" />
            <LoginForm emailInputRef={emailInputRef} />
            <div className="my-4 text-gray-500">
              <div className="mb-4">
                <span>Don't have an account? </span>
                <Link
                  to={signupPageUrl}
                  className="font-bold text-violet-600"
                >
                  Sign Up
                </Link>
              </div>
              <SignupLoginAgreement />
            </div>
          </Fragment>
        )}
      </div>
    </div>
  );
};

function checkInsecureBrowser () {
  const userAgentString: string = navigator.userAgent || navigator.vendor || window.opera;
  const matchers = ['Messenger', 'MESSENGER', 'FB', 'Twitter', 'Line', 'MicroMessenger', 'Puffin', 'Instagram'];
  return matchers.some(matcher => userAgentString.includes(matcher));
}
